import { ReactNode } from "react";
import { FormattedMessage } from "react-intl";
import type { PixiWizardSelection } from "../../hooks/usePixiWizardState";
import { resolveEffectiveDealId, resolveSelection } from "./selection";
import { FooterTotal, FooterTotalAmount, FooterTotalPeriod } from "./styles";
import { OnboardingPlans, planTotal } from "./useOnboardingPlans";

export interface PlanBillingFooter {
  /** Total price for the current selection, or null while plan data is missing. */
  footerStart: ReactNode;
  /** Whether the Plan & Billing step is complete enough to continue. */
  canContinue: boolean;
}

/**
 * Footer total + Continue gating for the Plan & Billing step. Derived from the
 * same plan data and resolved selection as the cards, so the footer and cards
 * can never disagree. Lives here (not in PixiWizardModal) to keep the wizard
 * shell step-agnostic — each step owns its own footer/gating.
 */
export const resolvePlanBillingFooter = (
  onboardingPlans: OnboardingPlans,
  state: PixiWizardSelection,
  isProTrialist: boolean,
): PlanBillingFooter => {
  const { planId, currency, billingCycle, licenses } = resolveSelection(
    state,
    isProTrialist,
  );
  const plan = onboardingPlans.getPlan(planId, billingCycle, currency);
  const monthlyPlan = onboardingPlans.getPlan(planId, "month", currency);
  // Derive from the valid deal, not the raw persisted dealId: a stale
  // free-device under an `Other` shipping country must not keep free-device
  // (monthly-rate) pricing in the footer total (GRWBKK-1730).
  const isFreeDevice = resolveEffectiveDealId(state) === "free-device";
  const canContinue =
    !onboardingPlans.loading && !onboardingPlans.error && Boolean(plan);

  const footerStart = plan ? (
    <FooterTotal data-testid="plan-billing-total">
      <FooterTotalAmount>
        {planTotal(plan, licenses, monthlyPlan, isFreeDevice)}
      </FooterTotalAmount>
      <FooterTotalPeriod>
        {billingCycle === "year" ? (
          <FormattedMessage
            id="pixi_wizard.plan.per_year"
            defaultMessage="per year"
          />
        ) : (
          <FormattedMessage
            id="pixi_wizard.plan.per_month"
            defaultMessage="per month"
          />
        )}
      </FooterTotalPeriod>
    </FooterTotal>
  ) : null;

  return { footerStart, canContinue };
};
